import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import { getBedAndBreakfasts } from '../../endpoints/bedandbreakfast';
import { createBedAndBreakfast } from '../../endpoints/bedandbreakfast';
import { BnBCreateRequest } from '../../types/bedandbreakfast';
import StateSelector from '../../Components/StateSelector';
import ChapterSelector from '../../Components/ChapterSelector';
import CountrySelector from '../../Components/CountrySelector';
import AlertItem from '../../Components/AlertItem';

interface FormData {
  name: string;
  description: string;
  city: string;
  country_id: string;
  state_id: string;
  local_chapter_id: string;
  amenities: string[];
  policies: string;
  cancellation_policy: string;
  pet_policy: string;
  smoking_policy: string;
  accessibility_features: string[];
  is_active: boolean;
}

const BnBCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    city: '',
    country_id: '',
    state_id: '',
    local_chapter_id: '',
    amenities: [],
    policies: '',
    cancellation_policy: '',
    pet_policy: '',
    smoking_policy: '',
    accessibility_features: [],
    is_active: true,
  });
  const [newAmenity, setNewAmenity] = useState('');
  const [newAccessibilityFeature, setNewAccessibilityFeature] = useState('');
  const [error, setError] = useState<string | null>(null);

  const {
    data: listGate,
    isLoading: gateLoading,
    isError: gateError,
    error: gateQueryError,
    refetch: refetchGate,
  } = useApiGet(
    ['bed-and-breakfasts', 'create-permission'],
    () =>
      getBedAndBreakfasts({
        page: 1,
        per_page: 1,
      }),
    {
      enabled: true,
      retry: 1,
    }
  );

  /** Fail closed: only explicit true from API allows create (matches BnB list button). */
  const mayCreate = listGate?.can_create_bnb === true;

  useEffect(() => {
    if (gateLoading || gateError) return;
    if (!mayCreate) {
      navigate('/bnb', { replace: true });
    }
  }, [gateLoading, gateError, mayCreate, navigate]);

  const createMutation = useApiSend(
    (data: BnBCreateRequest) => createBedAndBreakfast(data),
    (response) => {
      // Navigate to the newly created B&B detail page
      navigate(`/bnb/${response.id}`);
    },
    (error) => {
      setError(error?.message || 'Failed to create Bed & Breakfast');
    }
  );

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value: any) => {
    setFormData((prev) => ({
      ...prev,
      country_id: value?.id || '',
      state_id: '',
    }));
  };

  const handleStateChange = (value: any) => {
    setFormData((prev) => ({ ...prev, state_id: value?.id || '' }));
  };

  const handleChapterChange = (value: string | null) => {
    setFormData((prev) => ({ ...prev, local_chapter_id: value || '' }));
  };

  const handleAddAmenity = () => {
    if (newAmenity.trim()) {
      setFormData((prev) => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()],
      }));
      setNewAmenity('');
    }
  };

  const handleRemoveAmenity = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index),
    }));
  };

  const handleAddAccessibilityFeature = () => {
    if (newAccessibilityFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        accessibility_features: [
          ...prev.accessibility_features,
          newAccessibilityFeature.trim(),
        ],
      }));
      setNewAccessibilityFeature('');
    }
  };

  const handleRemoveAccessibilityFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      accessibility_features: prev.accessibility_features.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const isFormValid = (): boolean => {
    return !!(
      formData.name.trim() &&
      formData.city.trim() &&
      formData.state_id &&
      formData.local_chapter_id
    );
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }
    if (!mayCreate) {
      setError('You do not have permission to create a listing.');
      return;
    }

    const createRequest: BnBCreateRequest = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      city: formData.city.trim(),
      state_id: formData.state_id,
      amenities: formData.amenities,
      policies: formData.policies.trim() || null,
      cancellation_policy: formData.cancellation_policy.trim() || null,
      pet_policy: formData.pet_policy.trim() || null,
      smoking_policy: formData.smoking_policy.trim() || null,
      accessibility_features: formData.accessibility_features,
      local_chapter_id: formData.local_chapter_id,
      is_active: formData.is_active,
    };

    createMutation.mutate(createRequest);
  };

  if (gateLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (gateError) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <AlertItem
          severity="error"
          description={
            gateQueryError?.message ||
            'Could not verify permission to create a listing. Please try again.'
          }
          onClose={() => {}}
          keepOpen
        />
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={() => refetchGate()}>
            Retry
          </Button>
          <Button variant="outlined" onClick={() => navigate('/bnb')}>
            Back to directory
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!mayCreate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container sx={{ height: '100%' }}>
      <Box sx={{ p: 3, pb: 10 }}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/bnb')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight={600}>
            Create New Bed & Breakfast
          </Typography>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Box sx={{ mb: 3 }}>
            <AlertItem
              severity="error"
              description={error}
              onClose={() => setError(null)}
              keepOpen={true}
            />
          </Box>
        )}

        <Grid item container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <TextField
                    label="B&B Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    fullWidth
                    placeholder="Enter the name of the Bed & Breakfast"
                  />

                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange('description', e.target.value)
                    }
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Provide a detailed description"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) =>
                          handleInputChange('is_active', e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Location */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Location
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <TextField
                    label="City"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                    fullWidth
                    placeholder="Enter city name"
                  />

                  <CountrySelector
                    handleChange={handleCountryChange}
                    value={formData.country_id}
                  />

                  <StateSelector
                    handleChange={handleStateChange}
                    countryId={formData.country_id}
                    value={formData.state_id}
                    required
                  />

                  <ChapterSelector
                    chapter={formData.local_chapter_id}
                    spd={null}
                    keyId="bnb-create"
                    handleChapterSelect={handleChapterChange}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Amenities
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      label="Add Amenity"
                      value={newAmenity}
                      onChange={(e) => setNewAmenity(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAmenity();
                        }
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., WiFi, Parking, Kitchen"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddAmenity}
                      startIcon={<AddIcon />}
                    >
                      Add
                    </Button>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {formData.amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={amenity}
                        onDelete={() => handleRemoveAmenity(index)}
                        deleteIcon={<CloseIcon />}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Accessibility Features */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Accessibility Features
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      label="Add Feature"
                      value={newAccessibilityFeature}
                      onChange={(e) =>
                        setNewAccessibilityFeature(e.target.value)
                      }
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAccessibilityFeature();
                        }
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., Wheelchair Accessible"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddAccessibilityFeature}
                      startIcon={<AddIcon />}
                    >
                      Add
                    </Button>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {formData.accessibility_features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        onDelete={() => handleRemoveAccessibilityFeature(index)}
                        deleteIcon={<CloseIcon />}
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Policies */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Policies
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <TextField
                    label="General Policies"
                    value={formData.policies}
                    onChange={(e) =>
                      handleInputChange('policies', e.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="General house rules and policies"
                  />

                  <TextField
                    label="Cancellation Policy"
                    value={formData.cancellation_policy}
                    onChange={(e) =>
                      handleInputChange('cancellation_policy', e.target.value)
                    }
                    multiline
                    rows={2}
                    fullWidth
                    placeholder="Cancellation terms and conditions"
                  />

                  <TextField
                    label="Pet Policy"
                    value={formData.pet_policy}
                    onChange={(e) =>
                      handleInputChange('pet_policy', e.target.value)
                    }
                    multiline
                    rows={2}
                    fullWidth
                    placeholder="Pet-related rules"
                  />

                  <TextField
                    label="Smoking Policy"
                    value={formData.smoking_policy}
                    onChange={(e) =>
                      handleInputChange('smoking_policy', e.target.value)
                    }
                    multiline
                    rows={2}
                    fullWidth
                    placeholder="Smoking rules and restrictions"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/bnb')}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create B&B'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default BnBCreate;
